import React from 'react';
import { Link } from 'react-router-dom';
import { Anchor, Shield, Users, Compass, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const CONVICTIONS = [
  { icon: Compass, title: "Indépendance", text: "Aucune marque à vendre, aucun stock à écouler. Je défends votre projet, pas celui d'un vendeur." },
  { icon: Shield, title: "Transparence", text: "Je vous dis ce que je vérifie, ce que je ne vérifie pas, et vers qui vous orienter pour le reste." },
  { icon: Users, title: "Approche humaine", text: "Pragmatique et personnalisée, adaptée à votre projet et à votre niveau d'expérience." },
];

const AboutSection = () => {
  return (
    <section id="a-propos" className="py-20 md:py-28 bg-[#302f2c]" data-testid="about-section">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="mb-14">
          <p className="text-[#d9fb06] font-bold uppercase text-xl md:text-2xl tracking-widest">À propos</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-14 items-start">
          {/* Photo placeholder */}
          <div className="lg:col-span-2 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm">
              <div className="aspect-[4/5] rounded-2xl border-2 border-[#d9fb06]/30 overflow-hidden relative" data-testid="nicolas-photo">
                <img
                  src="/images/nicolas-daval.jpg?v=2"
                  alt="Nicolas Daval, expert nautique indépendant, sur un ponton du port de La Rochelle"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_60%,rgba(0,0,0,0.35))]"></div>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-3 space-y-5 text-[#dfddd6] leading-relaxed">
            <p className="text-lg">
              Je suis <strong className="text-[#dfddd6]">Nicolas Daval</strong>, fondateur de ValidatedBoat et conseiller nautique indépendant à La Rochelle.
            </p>
            <p>
              Depuis <strong className="text-[#dfddd6]">plus de 30 ans</strong>, j&apos;évolue dans les métiers de la vente, de la négociation et de l&apos;accompagnement client. Passionné par la mer et moi-même propriétaire de plusieurs bateaux, je connais les interrogations, les contraintes et les dépenses qui accompagnent l&apos;achat ou la vente d&apos;une unité de plaisance.
            </p>
            <p>
              J&apos;ai créé ValidatedBoat avec une conviction simple : <em>l&apos;achat d&apos;un bateau doit rester un projet enthousiasmant, mais il mérite d&apos;être préparé avec méthode, transparence et indépendance.</em>
            </p>
            <p>
              Je ne représente aucune marque et je ne cherche pas à vendre un bateau issu d&apos;un stock. Mon rôle consiste à <strong>défendre votre projet</strong> et à vous aider à prendre une décision éclairée : analyse d&apos;annonce, recherche personnalisée, préparation des visites, vérification des points de vigilance, négociation et orientation vers les professionnels compétents.
            </p>
            <p className="text-[#d9fb06] font-semibold italic border-l-4 border-[#d9fb06] pl-4">
              Avec ValidatedBoat, vous n&apos;achetez pas seulement un bateau : vous construisez un projet plus serein.
            </p>
            <div className="pt-4">
              <Button asChild data-testid="about-cta-btn"
                className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 font-bold uppercase text-sm">
                <Link to="/methode-validatedboat">
                  Découvrir la méthode <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Convictions */}
        <div className="grid md:grid-cols-3 gap-5 mt-16 max-w-5xl mx-auto">
          {CONVICTIONS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="bg-[#1a1c1b] border border-[#3f4816] rounded-2xl p-6 hover:border-[#d9fb06]/50 transition-colors">
                <div className="w-11 h-11 rounded-full bg-[#d9fb06]/10 flex items-center justify-center mb-4">
                  <Icon className="text-[#d9fb06]" size={20} />
                </div>
                <h3 className="text-lg font-bold text-[#d9fb06] uppercase mb-2">{c.title}</h3>
                <p className="text-sm text-[#888680] leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
